/**
 * Create a client (R13; EQCO-MODE-001).
 *
 * Only a short name is required. The register contract (section 2.1) is
 * explicit that a client missing detail is not blocked from EXISTING -- only
 * from being set up in the product that needs the detail, which is checked on
 * the client's own page ("Products for this client").
 *
 * THREE THINGS THIS PAGE DOES DELIBERATELY.
 *
 * 1. It picks the new client's id when it opens, and sends it with the create.
 *    A double click, a retry after a dropped response, or a resubmit all carry
 *    the same id, and the server answers the second with the client the first
 *    one made (EQCO spec 11.3). The button also locks -- but the id is what
 *    makes a duplicate impossible rather than unlikely.
 *
 * 2. Duplicates WARN, they do not block (R13). A group of companies can share
 *    a VAT number; the page shows the match and lets the person decide.
 *
 * 3. The client number is shown as "assigned when you create" -- it is never
 *    typed, by anyone.
 */

import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, type AuthState, type DuplicateMatch, type EntityType, type ObligationTypeInUse } from '../api'
import { AppShell } from '../components/AppShell'
import { ErrorState } from '../components/States'
import { vatTerm } from '../lib/vatTerms'

const ENTITY_TYPES = [
  ['individual', 'Individual'],
  ['company', 'Company'],
  ['cc', 'Close corporation'],
  ['trust', 'Trust'],
  ['partnership', 'Partnership'],
  ['deceased_estate', 'Deceased estate'],
  ['sole_proprietor', 'Sole proprietor'],
  ['npo', 'NPO'],
] as const

const INPUT =
  'w-full rounded-eq-sm border border-eq-border bg-white px-3 py-2 text-[14px] focus:border-eq-navy focus:outline-none'

const MATCH_WORDS = {
  registrationNumber: 'registration number',
  vatNumber: vatTerm('VAT_TERM_VAT_REGISTRATION_NUMBER'),
  registeredName: 'legal name',
  idNumber: 'ID number',
} as const

export function NewClientPage({
  obligationTypes,
  auth,
}: {
  obligationTypes: ObligationTypeInUse[]
  auth: AuthState
}) {
  const navigate = useNavigate()
  // One id for the life of this form -- see note 1.
  const requestId = useMemo(() => crypto.randomUUID(), [])

  const [shortName, setShortName] = useState('')
  const [entityType, setEntityType] = useState('')
  const [registeredName, setRegisteredName] = useState('')
  const [tradingName, setTradingName] = useState('')
  const [registrationNumber, setRegistrationNumber] = useState('')
  const [duplicates, setDuplicates] = useState<DuplicateMatch[]>([])
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // Look for duplicates a moment after typing stops, not on every key.
  useEffect(() => {
    if (registrationNumber.trim() === '' && registeredName.trim() === '') {
      setDuplicates([])
      return
    }
    const timer = window.setTimeout(() => {
      api
        .duplicates({ registrationNumber, registeredName })
        .then((r) => setDuplicates(r.matches))
        .catch(() => setDuplicates([]))
    }, 400)
    return () => window.clearTimeout(timer)
  }, [registrationNumber, registeredName])

  const dirty =
    shortName !== '' || entityType !== '' || registeredName !== '' || tradingName !== '' || registrationNumber !== ''

  // Leaving with typed-in details asks first (EQCO-MODE-003).
  useEffect(() => {
    if (!dirty || saving) return
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty, saving])

  const create = async () => {
    if (saving) return
    setSaving(true)
    setError(null)
    try {
      const created = await api.createClient({
        id: requestId,
        shortName,
        entityType: entityType === '' ? null : (entityType as EntityType),
        registeredName: registeredName.trim() === '' ? null : registeredName,
        tradingName: tradingName.trim() === '' ? null : tradingName,
        registrationNumber: registrationNumber.trim() === '' ? null : registrationNumber,
      })
      navigate(`/clients/${created.id}`, { replace: true })
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setSaving(false)
    }
  }

  const leave = (e: React.MouseEvent) => {
    if (dirty && !window.confirm('Leave without creating this client? What you typed will be lost.')) {
      e.preventDefault()
    }
  }

  return (
    <AppShell
      obligationTypes={obligationTypes}
      auth={auth}
      eyebrow="Clients"
      title="New client"
      subtitle="Only a short name is needed to create a client. What Accounts or Payroll need is checked when the client is set up in those products."
      action={
        <button
          type="button"
          onClick={() => void create()}
          disabled={saving || shortName.trim() === ''}
          className={[
            'rounded-eq-sm px-4 py-2 font-heading text-[13px] font-bold',
            saving || shortName.trim() === ''
              ? 'cursor-not-allowed bg-eq-grey text-eq-helper'
              : 'bg-eq-navy text-white',
          ].join(' ')}
        >
          {saving ? 'Creating…' : 'Create client'}
        </button>
      }
    >
      <p className="mb-4 text-[12.5px]">
        <Link to="/clients" onClick={leave} className="text-eq-navy underline">
          ← All clients
        </Link>
      </p>

      {error !== null && (
        <div className="mb-4">
          <ErrorState message={error} />
        </div>
      )}

      {duplicates.length > 0 && (
        <div role="alert" className="mb-4 rounded-eq-md border border-eq-warning bg-eq-warning-bg px-4 py-3 text-[14px]">
          <div className="font-semibold text-eq-warning">
            {duplicates.length === 1 ? 'A client with matching details already exists' : 'Clients with matching details already exist'}
          </div>
          <ul className="mt-1 list-none p-0">
            {duplicates.map((d) => (
              <li key={d.id}>
                <Link to={`/clients/${d.id}`} className="font-semibold text-eq-navy underline">
                  {d.clientNumber} · {d.shortName}
                </Link>{' '}
                <span className="text-eq-neutral">— same {d.matchedOn.map((m) => MATCH_WORDS[m]).join(' and ')}</span>
              </li>
            ))}
          </ul>
          <div className="mt-1 text-[12.5px] text-eq-neutral">
            Related entities can legitimately share details. Check it is not the same client, then create anyway if it is not.
          </div>
        </div>
      )}

      <section className="max-w-[56rem] rounded-eq-md border border-eq-border bg-white px-5 py-4">
        <div className="grid gap-4 md:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-[12.5px] font-semibold text-eq-neutral">Short name *</span>
            <input className={INPUT} value={shortName} onChange={(e) => setShortName(e.target.value)} required />
            <span className="mt-1 block text-[12px] text-eq-helper">How the practice refers to this client day to day.</span>
          </label>
          <label className="block">
            <span className="mb-1 block text-[12.5px] font-semibold text-eq-neutral">Entity type</span>
            <select className={INPUT} value={entityType} onChange={(e) => setEntityType(e.target.value)}>
              <option value="">Not set</option>
              {ENTITY_TYPES.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
            <span className="mt-1 block text-[12px] text-eq-helper">Decides which registrations apply.</span>
          </label>
          <label className="block">
            <span className="mb-1 block text-[12.5px] font-semibold text-eq-neutral">Legal name</span>
            <input className={INPUT} value={registeredName} onChange={(e) => setRegisteredName(e.target.value)} />
            <span className="mt-1 block text-[12px] text-eq-helper">As registered. Printed on tax invoices and formal reports.</span>
          </label>
          <label className="block">
            <span className="mb-1 block text-[12.5px] font-semibold text-eq-neutral">Trading name</span>
            <input className={INPUT} value={tradingName} onChange={(e) => setTradingName(e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-[12.5px] font-semibold text-eq-neutral">Registration number</span>
            <input className={INPUT} value={registrationNumber} onChange={(e) => setRegistrationNumber(e.target.value)} />
            <span className="mt-1 block text-[12px] text-eq-helper">Kept exactly as typed, slashes and leading zeros included.</span>
          </label>
          <div>
            <div className="mb-1 text-[12.5px] font-semibold text-eq-neutral">Client number</div>
            <div className="py-2 text-[14px] text-eq-helper">Assigned when you create. Never changes, never reused.</div>
          </div>
        </div>
      </section>
    </AppShell>
  )
}
